import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import TimeRangeFilter from './filters/TimeRangeFilter';
import CategoryFilter from './filters/CategoryFilter';
import SmartFilters from './filters/SmartFilters';
import { DEFAULT_FILTERS } from '../constants/filters';

/**
 * DashboardFilters - Inline filter bar with clear spacing.
 */
const DashboardFilters = ({
    currentFilters,
    onFilterChange,
    categories = [],
    showCategoryFilter = false,
    categoryLabel = 'Category',
    activeTab = 'overview'
}) => {
    const [showMore, setShowMore] = useState(false);
    const [countryOptions, setCountryOptions] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
                const response = await fetch(`${API_BASE_URL}/api/v1/locations/countries-only`);
                if (response.ok) {
                    const result = await response.json();
                    if (result.status === 'success' && Array.isArray(result.data)) {
                        const options = result.data
                            .map((country) => ({
                                value: String(country.id),
                                label: country.name_en || country.name
                            }))
                            .sort((a, b) => a.label.localeCompare(b.label));
                        setCountryOptions(options);
                    }
                }
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        if (activeTab === 'users') {
            fetchCountries();
        }
    }, [activeTab]);

    if (!currentFilters) return null;

    const hasSmartFilters = ['users', 'social', 'medical'].includes(activeTab);

    const handleTimeRangeChange = (value) => {
        if (value === 'custom') {
            onFilterChange({ ...currentFilters, timeRange: value });
        } else {
            onFilterChange({ ...currentFilters, timeRange: value, start_date: '', end_date: '' });
        }
    };

    const handleCategoryChange = (value) => {
        onFilterChange({ ...currentFilters, category: value });
    };

    const handleSmartFilterChange = (newFilters) => {
        onFilterChange(newFilters);
    };

    const handleReset = () => {
        onFilterChange(DEFAULT_FILTERS);
    };

    return (
        <div className="sd-filters">
            <TimeRangeFilter value={currentFilters.timeRange} onChange={handleTimeRangeChange} />

            {currentFilters.timeRange === 'custom' && (
                <div className="sd-date-range">
                    <Calendar size={14} className="sd-date-range__icon" />
                    <input
                        type="date"
                        value={currentFilters.start_date || ''}
                        onChange={(e) => onFilterChange({ ...currentFilters, start_date: e.target.value })}
                        className="sd-date-range__input"
                    />
                    <span className="sd-date-range__divider">-</span>
                    <input
                        type="date"
                        value={currentFilters.end_date || ''}
                        onChange={(e) => onFilterChange({ ...currentFilters, end_date: e.target.value })}
                        className="sd-date-range__input"
                    />
                </div>
            )}

            {showCategoryFilter && (
                <CategoryFilter
                    value={currentFilters.category}
                    options={categories}
                    label={categoryLabel}
                    onChange={handleCategoryChange}
                />
            )}

            {hasSmartFilters && (
                <SmartFilters
                    activeTab={activeTab}
                    filters={currentFilters}
                    countryOptions={countryOptions}
                    onChange={handleSmartFilterChange}
                    onReset={handleReset}
                    isOpen={showMore}
                    onToggle={() => setShowMore(!showMore)}
                />
            )}
        </div>
    );
};

export default DashboardFilters;
